import { useId, useLayoutEffect, useRef, type ReactNode } from 'react';

import { cn } from '@/lib/utils/cn';
import './Tabs.css';

export interface TabDefinition<T extends string> {
  value: T;
  label: string;
  icon?: ReactNode;
  /** Compteur affiché à droite du libellé (nombre de résultats). */
  count?: number;
}

interface TabsProps<T extends string> {
  id?: string;
  tabs: ReadonlyArray<TabDefinition<T>>;
  value: T;
  onChange: (value: T) => void;
  className?: string;
  label?: string;
  size?: 'sm' | 'md';
}

/**
 * Barre d'onglets.
 *
 * Implémente le motif ARIA « tabs » : `role="tablist"`, `aria-selected`, et
 * surtout la navigation aux flèches. Sans elle, un utilisateur au clavier doit
 * tabuler à travers chaque onglet — alors que la convention, sur ce composant,
 * est d'y entrer une fois puis de circuler aux flèches.
 *
 * `tabIndex={-1}` sur les onglets inactifs matérialise ce comportement : un
 * seul onglet est atteignable à la tabulation, les autres aux flèches.
 */
export const Tabs = <T extends string>({
  id,
  tabs,
  value,
  onChange,
  className,
  label = 'Vues disponibles',
  size = 'md',
}: TabsProps<T>) => {
  const generatedId = useId();
  const baseId = id ?? generatedId;
  const listRef = useRef<HTMLDivElement>(null);
  const indicatorRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const list = listRef.current;
    const indicator = indicatorRef.current;
    if (!list || !indicator) return;
    let frame = 0;

    const positionIndicator = () => {
      const active = list.querySelector<HTMLButtonElement>('[role="tab"][aria-selected="true"]');
      if (!active || !active.offsetWidth || !active.offsetHeight) {
        delete list.dataset.indicatorReady;
        return;
      }

      // Coordonnées dans le contenu défilant : le repère suit aussi le scroll horizontal.
      indicator.style.setProperty('--tab-x', `${active.offsetLeft}px`);
      indicator.style.setProperty('--tab-y', `${active.offsetTop}px`);
      indicator.style.setProperty('--tab-width', `${active.offsetWidth}px`);
      indicator.style.setProperty('--tab-height', `${active.offsetHeight}px`);

      if (list.dataset.indicatorReady !== 'true') {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          list.dataset.indicatorReady = 'true';
        });
      }
    };

    positionIndicator();
    // Les compteurs et les polices peuvent modifier la largeur des onglets après le rendu.
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(positionIndicator);
    observer?.observe(list);
    list.querySelectorAll('[role="tab"]').forEach((element) => observer?.observe(element));
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
    };
  }, [value, tabs, size]);

  const handleKeyDown = (event: React.KeyboardEvent, index: number): void => {
    const offset = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    const isJump = event.key === 'Home' || event.key === 'End';

    if (offset === 0 && !isJump) return;

    event.preventDefault();

    // Le modulo boucle d'un bout à l'autre : après le dernier onglet, on
    // revient au premier — comportement attendu du motif ARIA.
    const nextIndex =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? tabs.length - 1
          : (index + offset + tabs.length) % tabs.length;

    const next = tabs[nextIndex];
    if (!next) return;

    onChange(next.value);
    document.getElementById(`${baseId}-${next.value}`)?.focus();
  };

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={label}
      className={cn(
        'tabs-list border-navy-200/70 relative isolate inline-flex max-w-full items-center gap-1 overflow-x-auto rounded-[10px] border bg-navy-50/60 p-1 shadow-[inset_0_1px_0_0_rgb(255_255_255/0.95)]',
        className,
      )}
    >
      <span
        ref={indicatorRef}
        aria-hidden="true"
        className="tabs-indicator border-accent-200/70 bg-accent-50 pointer-events-none absolute top-0 left-0 rounded-lg border bg-linear-to-b from-white/70 to-transparent shadow-[inset_0_1px_0_0_rgb(255_255_255/0.95),0_1px_3px_rgb(15_42_95/0.06)]"
      />
      {tabs.map((tab, index) => {
        const isActive = tab.value === value;

        return (
          <button
            key={tab.value}
            id={`${baseId}-${tab.value}`}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={`${baseId}-${tab.value}-panel`}
            tabIndex={isActive ? 0 : -1}
            onClick={() => {
              onChange(tab.value);
            }}
            onKeyDown={(event) => {
              handleKeyDown(event, index);
            }}
            className={cn(
              'tabs-trigger relative z-10 flex min-h-10 shrink-0 cursor-pointer items-center gap-2 rounded-lg border border-transparent py-2 font-semibold whitespace-nowrap',
              size === 'sm' ? 'px-3 text-[13px]' : 'px-3 text-sm sm:px-4',
              'transition-[color,background-color,border-color,box-shadow] duration-200 motion-reduce:transition-none',
              'focus-visible:ring-accent-700/25 focus-visible:ring-2 focus-visible:ring-inset focus-visible:outline-none',
              isActive ? 'text-accent-800' : 'text-ink-500 hover:bg-white/70 hover:text-navy-900',
            )}
          >
            {tab.icon}
            {tab.label}

            {tab.count !== undefined ? (
              <span
                className={cn(
                  'rounded-md px-1.5 py-0.5 text-[11px] font-semibold tabular-nums transition-colors duration-200 motion-reduce:transition-none',
                  isActive ? 'bg-white/80 text-accent-700' : 'bg-navy-100/60 text-ink-500',
                )}
              >
                {tab.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
};

/** Les identifiants relient explicitement le panneau à son onglet. */
export const TabPanel = ({
  children,
  id,
  labelledBy,
  hidden = false,
}: {
  children: ReactNode;
  id: string;
  labelledBy: string;
  hidden?: boolean;
}) => (
  <div
    key={id}
    id={id}
    role="tabpanel"
    hidden={hidden}
    aria-labelledby={labelledBy}
    tabIndex={0}
    className="tabs-panel rounded-[10px] focus-visible:outline-accent-700/40 focus-visible:outline-2 focus-visible:outline-offset-4"
  >
    {children}
  </div>
);
